import * as React from "react";
import DOMPurify from "dompurify";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { mailLinkZiel, oeffneImNeuenTab, type MailtoZiel } from "./mailLinks";
import { berechneSkalierung, MAIL_UMBRUCH_CSS } from "./mailBreiteAnpassen";
import { zitatAbtrennen as zitatImDokumentAbtrennen } from "./mailZitat";
import { fingerAbstand, istDoppeltipp, naechsterZoom, scrollNachZoom } from "./mailZoom";

type Props = {
  html: string;
  className?: string;
  minHeight?: number;
  /**
   * SP4/A2: Externe (Remote-)Inhalte neutralisieren, bis der Nutzer sie
   * ausdrücklich anzeigt. `true` (Default) blockiert Remote-`src`/`srcset`/
   * `background`/CSS-`url()`/`@import`; `false` rendert das Original.
   *
   * `cid:` und `data:` werden NIE blockiert (SP6 braucht sie fürs
   * Inline-Bild-Anzeigen, Konsistenz-Plan P0-A).
   */
  blockRemote?: boolean;
  /**
   * Wird gemeldet, sobald feststeht, ob im aktuell gerenderten HTML externe
   * Inhalte vorkamen. Der Aufrufer kann damit das „Externe Inhalte blockiert"-
   * Banner ein-/ausblenden.
   */
  onRemotePresence?: (hasRemote: boolean) => void;
  /**
   * Wie viele Bilder gerade noch geladen werden (0 = fertig).
   *
   * Ohne diese Rückmeldung sah der Nutzer nach „Externe Inhalte anzeigen" nur
   * eine Weile lang Lücken im Text und hielt die Mail für kaputt — Bilder
   * kommen bei Signaturen und Newslettern von fremden Servern und brauchen
   * ihre Zeit. Der Aufrufer zeigt daraus „Bilder werden geladen …".
   */
  onBilderLaden?: (anzahl: number) => void;
  /**
   * T-36: Der Nutzer hat im Mailtext etwas markiert (`null` = Markierung weg).
   * `rect` ist die Markierung in Koordinaten des ÄUSSEREN Fensters (fixed),
   * `kontextmenue` = per Rechtsklick ausgelöst. Der Rechtsklick wird nur bei
   * nicht leerer Markierung abgefangen — sonst bleibt das Browsermenü.
   * Gelesen wird reiner Text (`Selection.toString()`); der iframe bekommt
   * dafür KEIN `allow-scripts`, die Listener hängt der Parent an.
   */
  onMarkierung?: (m: MailMarkierung | null) => void;
  /**
   * T-236: Klick bzw. Rechtsklick auf ein im Mailtext EINGEBETTETES Bild
   * (`data:`/`blob:`-Quelle; fremde Server-Bilder lösen nichts aus). Der Klick
   * öffnet beim Aufrufer die Vorschau, der Rechtsklick sein Menü — das
   * Browsermenü wird nur dann abgefangen. Ein Bild in einem Link bleibt beim
   * Link, eine Textmarkierung beim Markierungsmenü (T-36).
   */
  onBild?: (b: MailBild) => void;
  /**
   * Klick auf einen `mailto:`-Link im Mailtext. Ohne Angabe öffnet sich das
   * KMVS-Verfassen-Fenster (sofern ein WindowManager da ist), sonst das
   * Standard-Mailprogramm. Web-Links öffnen IMMER in einem neuen Tab
   * (s. `mailLinks.ts`).
   */
  onMailto?: (m: MailtoZiel) => void;
  /**
   * Breite Mails (Tabellen-Layouts, große Bilder) auf die verfügbare Breite
   * bringen statt sie rechts abzuschneiden: erst umbrechen, dann — wie Outlook
   * mobil — verkleinern (s. `mailBreiteAnpassen.ts`). Der Lesebereich setzt es.
   */
  anBreiteAnpassen?: boolean;
  /**
   * Handy: zitierte Verläufe (Outlook, Gmail, Apple Mail …) bleiben sichtbar,
   * eine Trennlinie setzt sie vom neuen Text ab — wie Outlook iOS (`mailZitat.ts`).
   */
  zitatAbtrennen?: boolean;
  /**
   * Handy: Zwei-Finger-Zoom NUR im Mailtext (1–3×, optisch, seitlich
   * verschiebbar); Doppeltipp setzt zurück. Der Seiten-Zoom ist auf iOS aus.
   */
  zoombar?: boolean;
};

/** T-236: ein eingebettetes Bild im Mailtext. */
export type MailBild = {
  /** Content-Id aus `data-kmvs-cid` — `null`, wenn das Bild keinem Anhang zuzuordnen ist. */
  cid: string | null;
  dataUri: string;
  /** Mausposition in Koordinaten des ÄUSSEREN Fensters (fixed). */
  x: number;
  y: number;
  kontextmenue: boolean;
};

/** T-36: eine Markierung im Mailtext. */
export type MailMarkierung = {
  text: string;
  rect: { top: number; left: number; bottom: number; right: number } | null;
  kontextmenue: boolean;
};

/**
 * Nach dieser Zeit gilt der Ladevorgang als beendet, auch wenn ein Bild weder
 * `load` noch `error` gemeldet hat.
 *
 * Ein fremder Server, der die Verbindung offen hält, ohne zu antworten, ist
 * keine Seltenheit — ohne diesen Schlussstrich bliebe „Bilder werden geladen …"
 * für immer stehen und wäre damit schlimmer als gar kein Hinweis.
 */
export const BILD_LADE_FRIST_MS = 20_000;

/**
 * SP4 — EIN Scroll-Kontext: reine, testbare Höhen-Berechnung für den Mail-iframe.
 *
 * Bekommt die im `contentDocument` gemessenen Höhen (in beliebiger Reihenfolge,
 * `null`/`undefined`/0/NaN werden ignoriert) und liefert die iframe-Höhe, die den
 * gesamten Inhalt zeigt: größte valide Messung + kleiner Puffer gegen Subpixel-
 * Rundung (damit `scrolling="no"` nichts abschneidet). Liegt keine valide Messung
 * vor, `null` → der Aufrufer lässt die `minHeight` greifen.
 *
 * Bewusst pur (keine DOM-Abhängigkeit), damit es im Node-Test-Runner läuft.
 */
export function computeMailBodyHeight(
  measurements: Array<number | null | undefined>,
  buffer = 2,
): number | null {
  let max = 0;
  for (const m of measurements) {
    if (typeof m === "number" && Number.isFinite(m) && m > 0 && m > max) max = m;
  }
  // Aufrunden: `getBoundingClientRect()` liefert Fließkomma, und eine abgerundete
  // Höhe ist genau die halbe Zeile, die unten fehlt.
  return max > 0 ? Math.ceil(max + buffer) : null;
}

/**
 * Rechnet den AUSSENRAND auf eine am `<body>` genommene Messung.
 *
 * Der Grund für den gemeldeten Fehler „der Text unten ist abgehackt": Die
 * vertikalen Margins des `<body>` (Browser-Default 8px oben und unten, denn das
 * srcDoc ist ein reines Fragment ohne eigenes `<body>`) liegen AUSSERHALB jeder
 * Body-Messung — `scrollHeight`, `offsetHeight` und `getBoundingClientRect()`
 * enden alle an der Rahmenkante. Der iframe fiel dadurch zu niedrig aus und
 * schnitt mit `scrolling="no"` die letzten Zeilen ab.
 *
 * Nur auf Body-Messungen anwenden: `documentElement.offsetHeight` misst die
 * html-Box und trägt diese Margins bereits, dort würden sie doppelt zählen.
 * Eine Messung von 0 (leere Mail) bleibt 0, sonst fiele der iframe statt auf
 * die `minHeight` des Aufrufers auf die Höhe zweier Margins zusammen.
 */
export function plusAussenrand(
  messung: number | null | undefined,
  aussenrand: number,
): number | null {
  if (typeof messung !== "number" || !Number.isFinite(messung) || messung <= 0) return null;
  const rand = Number.isFinite(aussenrand) && aussenrand > 0 ? aussenrand : 0;
  return messung + rand;
}

/**
 * Markiert eine URL als „remote" (= muss blockiert werden) bzw. „lokal".
 *
 * Lokal (DURCHLASSEN, nie blockieren):
 *   - `cid:` — Inline-Anhänge (SP6 löst sie später auf).
 *   - `data:` — eingebettete Inline-Daten (z. B. data:image/png;base64,…).
 *   - relative/anker-Verweise (`#…`, leer) — kein Netzwerk-Beacon.
 * Remote (BLOCKIEREN): alles mit Host — http(s):, protokoll-relativ `//host`,
 * oder eine sonstige absolute URL.
 */
function isRemoteUrl(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const url = raw.trim();
  if (!url) return false;
  const lower = url.toLowerCase();
  // Inline / lokal → durchlassen.
  if (lower.startsWith("cid:")) return false;
  if (lower.startsWith("blob:")) return false;
  if (lower.startsWith("data:")) return false;
  if (lower.startsWith("#")) return false;
  // Protokoll-relativ `//host/...` → remote.
  if (url.startsWith("//")) return true;
  // Absolute URL mit Schema (http:, https:, ftp:, …) → remote.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return true;
  // Sonst relativ (kein Host, kein Schema) → kein externer Abruf.
  return false;
}

/** Hat ein `srcset` mindestens einen Remote-Kandidaten? */
function srcsetHasRemote(srcset: string): boolean {
  return srcset
    .split(",")
    .map((c) => c.trim().split(/\s+/)[0])
    .some((u) => isRemoteUrl(u));
}

/**
 * Ersetzt Remote-`url(...)` in einem CSS-Wert durch eine harmlose Konstante und
 * meldet, ob etwas neutralisiert wurde. `cid:`/`data:`-`url()` bleiben erhalten.
 */
function neutralizeCssUrls(css: string): { css: string; blocked: boolean } {
  let blocked = false;
  const out = css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, _q, ref: string) => {
    if (isRemoteUrl(ref)) {
      blocked = true;
      return "url()"; // Beacon entschärft, Layout-Wert bleibt syntaktisch gültig.
    }
    return match;
  });
  return { css: out, blocked };
}

/**
 * Neutralisiert `@import "remote-url"` in einem `<style>`-Block.
 */
function neutralizeCssImports(css: string): { css: string; blocked: boolean } {
  let blocked = false;
  const out = css.replace(
    /@import\s+(?:url\(\s*)?(['"]?)([^'")\s;]+)\1\s*\)?[^;]*;?/gi,
    (match, _q, ref: string) => {
      if (isRemoteUrl(ref)) {
        blocked = true;
        return ""; // Remote-Stylesheet-Import komplett entfernen.
      }
      return match;
    },
  );
  return { css: out, blocked };
}

/**
 * DOMPurify-Hook (P0-A PRIMÄRmechanismus): neutralisiert beim Sanitizing alle
 * Remote-Beacons im Markup und verschiebt sie nach `data-blocked-*`, damit
 * dieselbe Mail bei „Anzeigen" mit Original-Quellen erneut sanitisiert und
 * gerendert werden kann.
 *
 * Wird global einmalig registriert (Idempotenz über ein Flag); die `blocked`-
 * Erkennung läuft über einen Closure-Counter, der je `sanitize`-Aufruf
 * zurückgesetzt wird.
 */
let blockedCounter = 0;
let hookRegistered = false;
// Steuert, ob der Hook aktuell neutralisieren soll. Im „Anzeigen"-Durchlauf
// (block=false) ist er aus, damit die wiederhergestellten Remote-Quellen
// erhalten bleiben.
let hookActive = true;
// SP3: Steuert, ob geblockte <img> einen neutralen Platzhalter bekommen.
// Nur der LESE-Pfad (SandboxedMailBody) aktiviert das — der Reply-Quote-Pfad
// (buildReplyQuote) bleibt unangetastet, damit kein Platzhalter-Bild in
// ausgehende Mails gerät.
let placeholderActive = false;

/**
 * SP3 — neutraler Platzhalter für geblockte Remote-Bilder: ohne `src` rendern
 * Browser die Broken-Image-Glyphe (das „X" aus Screenshot 16.07.39, Verwechs-
 * lungsgefahr mit einem Entfernen-Button). Stattdessen bekommt ein geblocktes
 * `<img>` dieses ruhige Inline-SVG (Bildsymbol auf warmem Hellgrau); die
 * `data:`-URI passiert die Block-CSP (`img-src cid: data:`). Beim „Anzeigen"-
 * Restore wird der Platzhalter von der Original-Quelle überschrieben bzw.
 * wieder entfernt.
 */
const BLOCKED_IMG_PLACEHOLDER =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="60" viewBox="0 0 80 60">' +
      '<rect width="80" height="60" rx="6" fill="#f3f0f5"/>' +
      '<g stroke="#b9aebf" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="24" y="16" width="32" height="26" rx="3"/>' +
      '<circle cx="33" cy="25" r="3"/>' +
      '<path d="M27 39 L36 30 L43 37 L48 32 L54 38"/>' +
      "</g></svg>",
  );

function ensureHookRegistered() {
  if (hookRegistered) return;
  hookRegistered = true;
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    const passiveImage = (node.nodeName === "IMG" && data.attrName === "src") ||
      (["TABLE", "TD", "TH", "BODY"].includes(node.nodeName) && data.attrName === "background");
    if (passiveImage && data.attrValue.startsWith("blob:")) data.forceKeepAttr = true;
  });
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (!hookActive) return;
    if (!(node instanceof Element)) return;

    // 1) src / poster — Remote → blocken, cid:/data: durchlassen.
    for (const attr of ["src", "poster"]) {
      const val = node.getAttribute(attr);
      if (val && isRemoteUrl(val)) {
        node.setAttribute(`data-blocked-${attr}`, val);
        node.removeAttribute(attr);
        blockedCounter++;
      }
    }

    // 2) srcset — nur blocken, wenn ein Remote-Kandidat enthalten ist.
    const srcset = node.getAttribute("srcset");
    if (srcset && srcsetHasRemote(srcset)) {
      node.setAttribute("data-blocked-srcset", srcset);
      node.removeAttribute("srcset");
      blockedCounter++;
    }

    // 2b) SP3: geblocktes <img> ohne verbliebene Quelle → neutraler Platzhalter
    // statt Browser-Broken-Image-„X". Der Marker `data-blocked-placeholder`
    // erlaubt dem Restore, den Platzhalter sauber wieder zu entfernen.
    if (
      placeholderActive &&
      node.tagName === "IMG" &&
      !node.getAttribute("src") &&
      (node.hasAttribute("data-blocked-src") || node.hasAttribute("data-blocked-srcset"))
    ) {
      node.setAttribute("src", BLOCKED_IMG_PLACEHOLDER);
      node.setAttribute("data-blocked-placeholder", "1");
    }

    // 3) background-Attribut (Legacy-HTML-Mails).
    const bg = node.getAttribute("background");
    if (bg && isRemoteUrl(bg)) {
      node.setAttribute("data-blocked-background", bg);
      node.removeAttribute("background");
      blockedCounter++;
    }

    // 4) Inline-style url() (z. B. background-image:url(http…)).
    const style = node.getAttribute("style");
    if (style && /url\(/i.test(style)) {
      const { css, blocked } = neutralizeCssUrls(style);
      if (blocked) {
        node.setAttribute("data-blocked-style", style);
        node.setAttribute("style", css);
        blockedCounter++;
      }
    }

    // 5) <style>-Blöcke: @import + url() neutralisieren.
    if (node.tagName === "STYLE" && node.textContent) {
      const imp = neutralizeCssImports(node.textContent);
      const urls = neutralizeCssUrls(imp.css);
      if (imp.blocked || urls.blocked) {
        node.textContent = urls.css;
        blockedCounter++;
      }
    }
  });
}

/**
 * Reaktiviert zuvor geblockte Remote-Quellen: stellt `src`/`srcset`/`background`/
 * `style` aus den `data-blocked-*`-Attributen wieder her. Wird nach dem
 * „Anzeigen"-Klick angewandt (dann mit `blockRemote=false` neu sanitisiert).
 */
const ALLOWED_BLOCKED_DATA_ATTRS = [
  "data-blocked-src",
  "data-blocked-poster",
  "data-blocked-srcset",
  "data-blocked-background",
  "data-blocked-style",
  // SP3: markiert ein <img>, dessen src der neutrale Block-Platzhalter ist.
  "data-blocked-placeholder",
];

/**
 * Bereinigt und neutralisiert ein Mail-HTML.
 *
 * Zweischichtige XSS-Abwehr (F-01) BLEIBT erhalten: DOMPurify entfernt
 * `<script>`, `on*`-Attribute, `javascript:`-URIs etc., und der iframe läuft
 * ohne `allow-scripts`.
 *
 * Zusätzlich (SP4/A2):
 *  - `blockRemote=true`: Remote-Beacons werden über den Hook nach
 *    `data-blocked-*` verschoben (kein Netzwerk-Abruf beim Rendern).
 *  - `blockRemote=false`: vor dem Sanitizing werden die geblockten Quellen aus
 *    `data-blocked-*` zurückgeschrieben, sodass die Originale geladen werden.
 *
 * `cid:`/`data:`-Quellen passieren in BEIDEN Modi unangetastet (P0-A) — SP6
 * baut darauf auf, um Inline-Bilder anzuzeigen.
 *
 * Wird auch von SP2 (Reply-Quote-Sanitizing) genutzt → exportiert.
 */
export function blockRemoteContent(
  html: string,
  opts: { block?: boolean; placeholder?: boolean } = {},
): { html: string; blocked: boolean } {
  ensureHookRegistered();
  const block = opts.block !== false; // Default: blocken.
  blockedCounter = 0;
  hookActive = true;
  // SP3: Platzhalter nur auf ausdrücklichen Wunsch des Aufrufers (Lese-Pfad) —
  // der Reply-Quote-Pfad (Send-HTML) bleibt byte-identisch zu vorher.
  placeholderActive = block && opts.placeholder === true;

  const sanitized = DOMPurify.sanitize(html ?? "", {
    USE_PROFILES: { html: true },
    // Belt and suspenders: explizit alles blocken, was Code-Ausführung erlauben
    // könnte, falls sich DOMPurify-Defaults je ändern.
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction"],
    // Unsere `data-blocked-*`-Marker müssen erhalten bleiben (Default-Allow für
    // data-*; explizit gelistet zur Dokumentation/Robustheit).
    ADD_ATTR: ALLOWED_BLOCKED_DATA_ATTRS,
  });

  if (block) {
    return { html: sanitized, blocked: blockedCounter > 0 };
  }

  // „Anzeigen": geblockte Quellen aus den data-Markern zurückschreiben und
  // erneut sanitisieren — diesmal mit deaktiviertem Block-Hook, damit die
  // wiederhergestellten Remote-Quellen erhalten bleiben.
  const restored = restoreBlocked(sanitized);
  hookActive = false;
  try {
    const finalHtml = DOMPurify.sanitize(restored, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction"],
    });
    return { html: finalHtml, blocked: false };
  } finally {
    hookActive = true;
  }
}

/**
 * Schreibt `data-blocked-*` zurück auf die echten Attribute. Läuft über einen
 * Off-DOM-Container, sodass beim Parsen keine Netzwerk-Abrufe ausgelöst werden
 * (das passiert erst beim finalen iframe-Render).
 */
function restoreBlocked(html: string): string {
  if (typeof document === "undefined") return html;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const mapping: Array<[string, string]> = [
    ["data-blocked-src", "src"],
    ["data-blocked-poster", "poster"],
    ["data-blocked-srcset", "srcset"],
    ["data-blocked-background", "background"],
    ["data-blocked-style", "style"],
  ];
  tpl.content.querySelectorAll("*").forEach((el) => {
    for (const [dataAttr, realAttr] of mapping) {
      const v = el.getAttribute(dataAttr);
      if (v !== null) {
        el.setAttribute(realAttr, v);
        el.removeAttribute(dataAttr);
      }
    }
    // SP3: Platzhalter aufräumen. Hatte das <img> ein geblocktes `src`, wurde
    // der Platzhalter oben bereits von der Original-Quelle überschrieben; war
    // NUR das `srcset` geblockt, stünde sonst der Platzhalter als src im Weg.
    if (el.hasAttribute("data-blocked-placeholder")) {
      if (el.getAttribute("src") === BLOCKED_IMG_PLACEHOLDER) el.removeAttribute("src");
      el.removeAttribute("data-blocked-placeholder");
    }
  });
  return tpl.innerHTML;
}

/**
 * CSP-meta, die zusätzlich (Defense-in-Depth) in das iframe-`srcDoc` injiziert
 * wird, um im Block-Modus auch dann keine externen Ressourcen zu laden, falls
 * der DOM-Hook eine Quelle übersehen sollte. Der DOM-Hook bleibt der
 * PRIMÄRmechanismus (P0-A).
 */
export const BLOCK_CSP_META =
  `<meta http-equiv="Content-Security-Policy" ` +
  `content="default-src 'none'; img-src cid: data: blob:; style-src 'unsafe-inline'; font-src cid: data:; media-src cid: data:;">`;

/**
 * Renders untrusted mail HTML inside a sandboxed iframe with a DOMPurify
 * pass *before* injection. Two-layer defense against the XSS finding F-01
 * (4sight audit, 30.04.2026):
 *
 *   1. DOMPurify strips <script>, on*-attributes, javascript: / data: URIs,
 *      <style> with expressions, etc. Default HTML profile.
 *   2. The iframe runs without `allow-scripts`, so even if a sanitization
 *      bypass slipped through DOMPurify, foreign JS cannot execute inside
 *      the frame. `allow-same-origin` is set so the parent can read the
 *      iframe's document and adjust the height — that combination (origin
 *      *with* same-origin but *without* scripts) keeps foreign JS blocked
 *      while letting our own measurement code work.
 *
 * MSAL/better-auth bearer tokens in the parent's localStorage are therefore
 * out of reach for any script in the rendered mail body.
 *
 * SP4/A2: Solange `blockRemote` gesetzt ist, werden externe Bilder/Hintergründe
 * nicht geladen (Tracking-/Beacon-Schutz). Der Aufrufer rendert das Banner
 * „Externe Inhalte blockiert — Anzeigen" darüber und schaltet `blockRemote` ab.
 */
export const SandboxedMailBody: React.FC<Props> = ({
  html,
  className,
  minHeight = 400,
  blockRemote = true,
  onRemotePresence,
  onBilderLaden,
  onMarkierung,
  onBild,
  onMailto,
  anBreiteAnpassen = false,
  zitatAbtrennen = false,
  zoombar = false,
}) => {
  const zitatRef = React.useRef(zitatAbtrennen);
  zitatRef.current = zitatAbtrennen;
  const zoombarRef = React.useRef(zoombar);
  zoombarRef.current = zoombar;
  // Zoom: Hülle (scrollt seitlich) > Platzhalter (gezoomte Größe) > iframe
  // (Layoutbreite = Hülle, optisch skaliert). Bewusst ohne React-State — die
  // Geste setzt die Stile direkt, sonst ruckelt es bei jedem Fingerschritt.
  const huelleRef = React.useRef<HTMLDivElement | null>(null);
  const platzRef = React.useRef<HTMLDivElement | null>(null);
  const zoomRef = React.useRef(1);
  const wendeZoomAn = React.useCallback((z: number) => {
    zoomRef.current = z;
    const iframe = iframeRef.current;
    const platz = platzRef.current;
    const huelle = huelleRef.current;
    if (!iframe || !platz || !huelle) return;
    platz.style.width = `${z * 100}%`;
    platz.style.height = `${iframe.offsetHeight * z}px`;
    iframe.style.width = `${100 / z}%`;
    iframe.style.transform = z === 1 ? "" : `scale(${z})`;
    huelle.style.overflowX = z > 1 ? "auto" : "hidden";
  }, []);
  const anpassenRef = React.useRef(anBreiteAnpassen);
  anpassenRef.current = anBreiteAnpassen;
  const { html: sanitized, blocked } = React.useMemo(
    // SP3: `placeholder: true` — im Lese-iframe zeigen geblockte Bilder den
    // neutralen Platzhalter statt der Browser-Broken-Image-Glyphe.
    () => blockRemoteContent(html ?? "", { block: blockRemote, placeholder: true }),
    [html, blockRemote],
  );

  // Im Block-Modus zusätzlich die CSP-meta voranstellen (Defense-in-Depth).
  const srcDoc = React.useMemo(
    () => (blockRemote ? `${BLOCK_CSP_META}${sanitized}` : sanitized),
    [blockRemote, sanitized],
  );

  // Vorhandensein externer Inhalte nur EINMAL je Block-Zustand melden.
  const lastReported = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (!onRemotePresence) return;
    const present = blockRemote && blocked;
    if (lastReported.current !== present) {
      lastReported.current = present;
      onRemotePresence(present);
    }
  }, [onRemotePresence, blockRemote, blocked]);

  // ── SP4: EIN Scroll-Kontext ──────────────────────────────────────────────
  // Der iframe scrollt NIE intern (`scrolling="no"`), sondern wächst exakt auf
  // seine Inhaltshöhe; gescrollt wird ausschließlich der äußere Lese-Container
  // (`bodyScroll` in MailDetailWindow) — identisch im Panel- UND Fenster-Modus.
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const observerRef = React.useRef<ResizeObserver | null>(null);
  // Nachmess-Timer (s. onLoad) — beim nächsten Load und beim Unmount gelöscht.
  const timersRef = React.useRef<number[]>([]);
  // Bilder, die beim Load noch nicht fertig waren. Die Menge selbst ist die
  // Wahrheit; gemeldet wird ihre Größe.
  const offeneBilderRef = React.useRef<Set<Element>>(new Set());
  const bildFristRef = React.useRef<number | null>(null);
  // Über ein Ref gerufen, damit `onLoad` nicht bei jedem Render neu entsteht
  // (das würde den iframe unnötig oft neu anbinden).
  const bilderLadenRef = React.useRef(onBilderLaden);
  React.useEffect(() => {
    bilderLadenRef.current = onBilderLaden;
  }, [onBilderLaden]);

  // T-36: Markierung melden — über ein Ref, damit `onLoad` stabil bleibt.
  const markierungRef = React.useRef(onMarkierung);
  React.useEffect(() => {
    markierungRef.current = onMarkierung;
  }, [onMarkierung]);
  const markierungLoesenRef = React.useRef<(() => void) | null>(null);
  // T-236: Bild-Klick melden — ebenfalls über ein Ref.
  const bildRef = React.useRef(onBild);
  React.useEffect(() => {
    bildRef.current = onBild;
  }, [onBild]);

  // Links: `mailto:` → Verfassen-Fenster. Über ein Ref, damit `onLoad` stabil bleibt.
  const windowManager = useWindowManagerOptional();
  const mailtoRef = React.useRef<(m: MailtoZiel, url: string) => void>(() => {});
  mailtoRef.current = (m, url) => {
    if (onMailto) {
      onMailto(m);
      return;
    }
    const openWindow = windowManager?.openWindow;
    if (!openWindow) {
      window.location.href = url;
      return;
    }
    // Dynamisch: das Verfassen-Fenster zieht Editor & Co. nach — die
    // Hover-Vorschau und andere schlanke Aufrufer sollen das nicht laden müssen.
    void import("./ComposeMailWindow")
      .then(({ openComposeWindow }) =>
        openComposeWindow(openWindow, {
          to: m.to,
          ...(m.cc.length ? { cc: m.cc } : {}),
          ...(m.bcc.length ? { bcc: m.bcc } : {}),
          ...(m.subject ? { subject: m.subject } : {}),
          ...(m.body ? { body: m.body.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`).replace(/\r?\n/g, "<br>") } : {}),
        }),
      )
      .catch(() => {
        window.location.href = url;
      });
  };

  const meldeBilder = React.useCallback(() => {
    bilderLadenRef.current?.(offeneBilderRef.current.size);
  }, []);

  const syncHeight = React.useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      const body = doc?.body;
      const docEl = doc?.documentElement;
      if (!body || !docEl) return;
      // Feedback-Schutz: manche Mails erzwingen `html,body{height:100%}` — dann
      // würde `scrollHeight` die aktuelle iframe-Viewporthöhe zurückgeben und mit
      // jeder Messung um den Puffer wachsen. Voll-Höhen daher neutralisieren,
      // damit `scrollHeight` die reine INHALTS-Höhe misst (inline+important
      // schlägt Stylesheet-`!important` der Mail).
      docEl.style.setProperty("height", "auto", "important");
      body.style.setProperty("height", "auto", "important");
      // Dasselbe für `min-height` (`100%`/`100vh` löst gegen den Viewport auf):
      // bliebe es stehen, hinge die Messung an der zuletzt gesetzten iframe-Höhe
      // — der einzige Weg, auf dem die Höhe sich hochschaukeln könnte.
      docEl.style.setProperty("min-height", "0", "important");
      body.style.setProperty("min-height", "0", "important");
      // Die vertikalen MARGINS des Bodys liegen außerhalb jeder Body-Messung
      // (s. plusAussenrand) — genau die Pixel, die unten abgeschnitten wurden.
      const stil = doc?.defaultView?.getComputedStyle(body);
      const aussenrand = stil
        ? (parseFloat(stil.marginTop) || 0) + (parseFloat(stil.marginBottom) || 0)
        : 0;
      // Breite: was nach dem Umbruch noch übersteht, wird verkleinert. Vorher
      // die alte Skalierung lösen — sonst mäße die Breite die verkleinerte Fassung.
      let skalierung = 1;
      if (anpassenRef.current) {
        docEl.style.removeProperty("transform");
        const verfuegbar = docEl.clientWidth;
        // Zu breite Bilder zuerst auf die Breite bringen (nur DIESE: Abstands-
        // Bildchen mit fester Höhe sollen ihre Höhe behalten).
        if (verfuegbar > 0) {
          for (const img of Array.from(doc?.images ?? [])) {
            if (img.getBoundingClientRect().width > verfuegbar) {
              img.style.setProperty("max-width", "100%", "important");
              img.style.setProperty("height", "auto", "important");
            }
          }
        }
        skalierung = berechneSkalierung(docEl.scrollWidth, verfuegbar);
        if (skalierung < 1) {
          docEl.style.setProperty("transform-origin", "0 0");
          docEl.style.setProperty("transform", `scale(${skalierung})`);
        }
      }
      const next = computeMailBodyHeight([
        // Die html-Box umschließt die Body-Margins ebenso wie ein `margin-bottom`
        // des letzten Elements, das durch den Body hindurch nach außen kollabiert
        // ist (bei Mails mit `body{margin:0}` der Regelfall). Bewusst
        // `offsetHeight` und NICHT `scrollHeight`: nur letzteres hat beim
        // Wurzelelement den Viewport als Untergrenze und wäre die Ratsche, vor
        // der der Kommentar oben warnt.
        docEl.offsetHeight,
        // `getBoundingClientRect()` misst als Einziges mit Subpixel-Genauigkeit;
        // `scrollHeight` deckt zusätzlich Inhalt ab, der über den Body hinausragt.
        plusAussenrand(body.scrollHeight, aussenrand),
        plusAussenrand(body.offsetHeight, aussenrand),
        plusAussenrand(body.getBoundingClientRect().height, aussenrand),
      ]);
      // Die Verkleinerung ist nur optisch (transform) — die Höhe des iframe
      // schrumpft entsprechend mit, sonst stünde darunter leerer Raum.
      if (next != null) iframe.style.height = `${Math.ceil(next * skalierung)}px`;
      // Gezoomt: der Platzhalter wächst mit der Höhe mit.
      if (platzRef.current) platzRef.current.style.height = `${iframe.offsetHeight * zoomRef.current}px`;
    } catch {
      // null-origin Sandbox: kein Zugriff → iframe bleibt bei minHeight.
    }
  }, []);

  const onLoad = React.useCallback(() => {
    const iframe = iframeRef.current;
    // srcDoc-Wechsel (z. B. „Externe Inhalte anzeigen") = neuer Load → alten
    // Observer lösen, dann frisch messen und beobachten.
    observerRef.current?.disconnect();
    observerRef.current = null;
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
    markierungLoesenRef.current?.();
    markierungLoesenRef.current = null;
    // Umbruch-Regeln vor der ersten Messung einhängen (nur im Lesebereich).
    if (anpassenRef.current) {
      try {
        const d = iframe?.contentDocument;
        if (d && !d.getElementById("kmvs-umbruch")) {
          const st = d.createElement("style");
          st.id = "kmvs-umbruch";
          st.textContent = MAIL_UMBRUCH_CSS;
          (d.head ?? d.documentElement).appendChild(st);
        }
      } catch {
        /* null-origin: dann eben ohne */
      }
    }
    // Handy: Trennlinie vor dem zitierten Verlauf (vor der ersten Messung).
    if (zitatRef.current) {
      try {
        const d = iframe?.contentDocument;
        if (d) zitatImDokumentAbtrennen(d);
      } catch {
        /* null-origin: dann eben ohne Linie */
      }
    }
    // Handy: Zwei-Finger-Zoom im Mailtext. Die Berührungen landen im iframe-
    // Dokument (gleicher Ursprung, ohne Skripte darin) — die Listener hängt
    // der Elterncode an. `touch-action` überlässt Ein-Finger-Scrollen dem
    // Browser und nimmt ihm das eigene Pinch-Zoomen.
    if (zoombarRef.current) {
      try {
        const d = iframe?.contentDocument;
        if (d) {
          d.documentElement.style.setProperty("touch-action", "pan-x pan-y");
          let start: { zoom: number; abstand: number } | null = null;
          let letzterTipp: { zeit: number; x: number; y: number } | null = null;
          let bewegt = false;
          d.addEventListener(
            "touchstart",
            (e) => {
              bewegt = false;
              if (e.touches.length === 2) start = { zoom: zoomRef.current, abstand: fingerAbstand(e.touches[0], e.touches[1]) };
            },
            { passive: true },
          );
          d.addEventListener(
            "touchmove",
            (e) => {
              bewegt = true;
              if (e.touches.length !== 2 || !start) return;
              e.preventDefault();
              const vorher = zoomRef.current;
              const z = naechsterZoom(start.zoom, start.abstand, fingerAbstand(e.touches[0], e.touches[1]));
              if (z === vorher) return;
              const mitteX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
              const huelle = huelleRef.current;
              const links = huelle?.scrollLeft ?? 0;
              wendeZoomAn(z);
              if (huelle) huelle.scrollLeft = scrollNachZoom(links, mitteX, vorher, z);
            },
            { passive: false },
          );
          d.addEventListener("touchend", (e) => {
            if (e.touches.length < 2) start = null;
            if (e.touches.length > 0 || e.changedTouches.length !== 1 || bewegt) return;
            const t = e.changedTouches[0];
            const jetzt = { zeit: Date.now(), x: t.clientX, y: t.clientY };
            if (zoomRef.current > 1 && istDoppeltipp(letzterTipp, jetzt)) {
              wendeZoomAn(1);
              if (huelleRef.current) huelleRef.current.scrollLeft = 0;
              letzterTipp = null;
              return;
            }
            letzterTipp = jetzt;
          });
          // iOS Safari: die eigene Zoom-Geste unterdrücken (nur im Mailtext).
          const halt = (e: Event) => e.preventDefault();
          d.addEventListener("gesturestart", halt);
          d.addEventListener("gesturechange", halt);
        }
      } catch {
        /* null-origin: dann ohne Zoom */
      }
    }
    syncHeight();
    try {
      const doc = iframe?.contentDocument;
      // T-36: Markierung im Mailtext erfassen (nur lesend, nur Text).
      if (doc && iframe) {
        const lies = (kontextmenue: boolean): MailMarkierung | null => {
          const sel = iframe.contentWindow?.getSelection?.();
          const text = sel ? sel.toString() : "";
          if (!text.trim()) return null;
          let rect: MailMarkierung["rect"] = null;
          try {
            const r = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).getBoundingClientRect() : null;
            const f = iframe.getBoundingClientRect();
            if (r) {
              rect = { top: f.top + r.top, left: f.left + r.left, bottom: f.top + r.bottom, right: f.left + r.right };
            }
          } catch {
            rect = null;
          }
          return { text, rect, kontextmenue };
        };
        const beiMausHoch = () => markierungRef.current?.(lies(false));
        const beiTaste = (e: KeyboardEvent) => {
          if (e.key === "Escape") markierungRef.current?.(null);
          else if (e.shiftKey || e.key === "Shift" || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a")) {
            markierungRef.current?.(lies(false));
          }
        };
        const beiMausRunter = () => markierungRef.current?.(null);
        // T-236: eingebettetes Bild unter dem Mauszeiger (nicht in einem Link).
        const bildAn = (e: MouseEvent, kontextmenue: boolean): MailBild | null => {
          const ziel = e.target as Element | null;
          const img = ziel?.closest?.("img") as HTMLImageElement | null;
          // Der Platzhalter eines geblockten Fremdbilds ist auch eine `data:`-Quelle.
          if (!img || img.closest("a[href]") || img.hasAttribute("data-blocked-placeholder")) return null;
          const src = img.getAttribute("src") ?? "";
          if (!/^(data:image\/|blob:)/i.test(src)) return null;
          const f = iframe.getBoundingClientRect();
          return {
            cid: img.getAttribute("data-kmvs-cid") || null,
            dataUri: src,
            x: f.left + e.clientX,
            y: f.top + e.clientY,
            kontextmenue,
          };
        };
        const beiKontextmenue = (e: MouseEvent) => {
          // Eine Textmarkierung hat Vorrang (T-36), dann das Bild (T-236).
          const m = markierungRef.current ? lies(true) : null;
          if (m) {
            e.preventDefault();
            markierungRef.current?.(m);
            return;
          }
          const bild = bildRef.current ? bildAn(e, true) : null;
          if (!bild) return; // sonst: das normale Browsermenü
          e.preventDefault();
          bildRef.current?.(bild);
        };
        const beiKlick = (e: MouseEvent) => {
          if (!bildRef.current || e.button !== 0) return;
          // Wer gerade Text markiert hat, wollte nicht das Bild öffnen.
          if (iframe.contentWindow?.getSelection?.()?.toString().trim()) return;
          const bild = bildAn(e, false);
          if (bild) bildRef.current(bild);
        };
        // Links: nie im iframe navigieren (s. mailLinks.ts). Linksklick UND
        // Mittelklick/Strg-Klick landen hier; geöffnet wird aus dem Parent.
        const beiLink = (e: MouseEvent) => {
          if (e.type === "click" ? e.button !== 0 : e.button !== 1) return;
          const a = (e.target as Element | null)?.closest?.("a[href], area[href]");
          if (!a) return;
          e.preventDefault();
          const ziel = mailLinkZiel(a.getAttribute("href"));
          if (ziel.art === "extern") oeffneImNeuenTab(ziel.url);
          else if (ziel.art === "mailto") mailtoRef.current(ziel.mail, ziel.url);
          else if (ziel.art === "tel") window.location.href = ziel.url;
          else if (ziel.art === "anker" && ziel.id) {
            const sprung = doc.getElementById(ziel.id) ?? doc.getElementsByName(ziel.id)[0];
            sprung?.scrollIntoView?.({ block: "start" });
          }
        };
        // Für Rechtsklick → „Link in neuem Tab öffnen" und Tooltips: Web-Links
        // tragen sichtbar `target="_blank"` + `rel` (nur im angezeigten DOM,
        // das gesendete/zitierte HTML bleibt unberührt).
        doc.querySelectorAll("a[href], area[href]").forEach((a) => {
          if (mailLinkZiel(a.getAttribute("href")).art !== "extern") return;
          a.setAttribute("target", "_blank");
          a.setAttribute("rel", "noopener noreferrer");
        });
        doc.addEventListener("click", beiLink);
        doc.addEventListener("auxclick", beiLink);
        doc.addEventListener("mouseup", beiMausHoch);
        doc.addEventListener("keyup", beiTaste);
        doc.addEventListener("mousedown", beiMausRunter);
        doc.addEventListener("contextmenu", beiKontextmenue);
        doc.addEventListener("click", beiKlick);
        // Lupe als Zeiger: ohne sie ahnt niemand, dass das Bild anklickbar ist.
        if (bildRef.current) {
          doc.querySelectorAll("img").forEach((img) => {
            const src = img.getAttribute("src") ?? "";
            if (
              /^(data:image\/|blob:)/i.test(src) &&
              !img.closest("a[href]") &&
              !img.hasAttribute("data-blocked-placeholder")
            ) {
              (img as HTMLImageElement).style.cursor = "zoom-in";
            }
          });
        }
        markierungLoesenRef.current = () => {
          doc.removeEventListener("mouseup", beiMausHoch);
          doc.removeEventListener("keyup", beiTaste);
          doc.removeEventListener("mousedown", beiMausRunter);
          doc.removeEventListener("contextmenu", beiKontextmenue);
          doc.removeEventListener("click", beiKlick);
          doc.removeEventListener("click", beiLink);
          doc.removeEventListener("auxclick", beiLink);
        };
      }
      const body = doc?.body;
      if (body && typeof ResizeObserver !== "undefined") {
        // Spätere Layout-Änderungen (Remote-Bilder nach „Anzeigen", Fonts,
        // Bild-Reflow) führen die Höhe nach — statt einmaligem onLoad-Messschuss.
        const ro = new ResizeObserver(() => syncHeight());
        ro.observe(body);
        observerRef.current = ro;
      }
      // Noch nicht geladene Bilder: Sie sind gleichzeitig der Grund für eine
      // spätere Höhenänderung (Reflow) UND das, worüber der Nutzer Bescheid
      // wissen will — beides hängt am selben Ereignis.
      offeneBilderRef.current = new Set();
      doc?.querySelectorAll("img").forEach((img) => {
        if ((img as HTMLImageElement).complete) return;
        offeneBilderRef.current.add(img);
        const fertig = () => {
          offeneBilderRef.current.delete(img);
          meldeBilder();
          syncHeight();
        };
        img.addEventListener("load", fertig, { once: true });
        img.addEventListener("error", fertig, { once: true });
      });
      meldeBilder();
      if (bildFristRef.current != null) window.clearTimeout(bildFristRef.current);
      bildFristRef.current = offeneBilderRef.current.size
        ? window.setTimeout(() => {
            // Schlussstrich: siehe BILD_LADE_FRIST_MS.
            offeneBilderRef.current.clear();
            meldeBilder();
          }, BILD_LADE_FRIST_MS)
        : null;
      // Schriften: Steht beim `load` noch die Ersatzschrift, ändern sich Zeilen-
      // höhen erst, wenn die echte Schrift da ist — sonst bliebe der iframe auf
      // der Höhe der Ersatzschrift stehen und schnitte unten ab.
      doc?.fonts?.ready?.then(() => syncHeight()).catch(() => { /* egal */ });
      // Sicherheitsnetz: zwei späte Nachmessungen für Engines, deren
      // ResizeObserver den letzten Reflow nicht meldet. Kostet nichts, wenn die
      // Höhe schon stimmt (dieselbe Messung → derselbe Wert).
      timersRef.current.push(
        window.setTimeout(syncHeight, 150),
        window.setTimeout(syncHeight, 700),
      );
    } catch {
      // cross-origin: ignorieren (iframe bleibt bei minHeight).
    }
  }, [syncHeight, meldeBilder]);

  // Observer beim Unmount lösen (kein Leak beim Schließen des Panels/Fensters).
  React.useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      markierungLoesenRef.current?.();
      markierungLoesenRef.current = null;
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
      if (bildFristRef.current != null) window.clearTimeout(bildFristRef.current);
      bildFristRef.current = null;
      // Der Aufrufer soll den Hinweis nicht behalten, wenn es diesen Body nicht
      // mehr gibt (Karte zugeklappt, Fenster zu).
      offeneBilderRef.current.clear();
      bilderLadenRef.current?.(0);
    },
    [],
  );

  const rahmen = (
    <iframe
      ref={iframeRef}
      title="Mail body"
      sandbox="allow-same-origin"
      scrolling="no"
      srcDoc={srcDoc}
      className={className}
      style={{
        display: "block",
        width: "100%",
        minHeight,
        border: "none",
        background: "#fff",
        overflow: "hidden",
        ...(zoombar ? { transformOrigin: "0 0" } : null),
      }}
      onLoad={onLoad}
    />
  );
  if (!zoombar) return rahmen;
  return (
    <div ref={huelleRef} className="kmvs-mail-zoom" style={{ width: "100%", overflowX: "hidden", overflowY: "hidden" }}>
      <div ref={platzRef} style={{ position: "relative", width: "100%" }}>
        {rahmen}
      </div>
    </div>
  );
};

export default SandboxedMailBody;
